import { ComponentType, useState } from 'react';
import noop from 'utils/noop';
import {
  TRIGGER_METADATA,
  TriggerMetadata,
  ResourceType,
  startUtils,
  StartNodeData,
} from 'modules/workflows';
import {
  ResourceFieldSelect,
  ResourceFieldSelectProps,
} from 'modules/workflowNodeEditors/editorComponents/ResourceFieldSelect';
import { InternalOnChangeProp } from 'modules/workflowNodeEditors/components/StartNodeEditor/StartNodeEditor.types';
import { StyledLeftTooltip } from '../../../../EditorBody.styled';
import { ResourceComponentProps } from '../../../StartNodeEditor/resourceComponents/types';
import {
  ResourceListSelect,
  ResourceCustomEventSelect,
  ResourceCampaignSelect,
  ResourceMessageTypeSelect,
  ResourceChannelSelect,
  ViewListLink,
} from '../../../StartNodeEditor/resourceComponents';
import ResourceInput from './ResourceInput';

export const TRIGGER_DATA_ID = 'select-trigger-resource';
export const RESOURCE_ID = 'trigger-data';

type FieldComponent =
  | ComponentType<React.PropsWithChildren<ResourceComponentProps>>
  | ComponentType<React.PropsWithChildren<ResourceFieldSelectProps>>;

export const getFieldComponent = (resourceType?: ResourceType | null): FieldComponent => {
  switch (resourceType) {
    case ResourceType.List:
      return ResourceListSelect;
    case ResourceType.Campaign:
      return ResourceCampaignSelect;
    case ResourceType.MessageType:
      return ResourceMessageTypeSelect;
    case ResourceType.String:
      return ResourceInput;
    case ResourceType.ProfileField:
      return ResourceFieldSelect;
    case ResourceType.Channel:
      return ResourceChannelSelect;
    case ResourceType.CustomEvent:
      return ResourceCustomEventSelect;
    default:
      return ResourceInput;
  }
};

interface ExternalOnChangeProps extends InternalOnChangeProp {
  /** The name of the field to update */
  fieldName?: string;
  resourceName?: string;
}

interface ResourceFormProps {
  /** If true, show errors if needed */
  areValidationsShown?: boolean;
  /** The data from the start node */
  nodeData?: StartNodeData;
  /**
   * The selected trigger.
   * Eventually this should be equal to nodeData.nodeClass.
   */
  trigger?: string;
  /** Callback when resource has changed */
  onChange?: (args: ExternalOnChangeProps) => void;
  /** Display value to use other than triggerMetadata */
  displayValue?: string;
  /** is tile editor read only */
  isReadOnly?: boolean;
}

/**
 * NOTE: No longer used after StartTileRefresh is GA
 */
export const ResourceForm = ({
  areValidationsShown = false,
  nodeData,
  trigger,
  onChange = noop,
  displayValue,
  isReadOnly = false,
}: ResourceFormProps) => {
  const triggerMetadata: TriggerMetadata =
    trigger && TRIGGER_METADATA[trigger]
      ? TRIGGER_METADATA[trigger]
      : ({} as unknown as TriggerMetadata);
  const {
    resourceType,
    selectPlaceholder,
    resourceValueType = '',
    resourceHasLink = false,
  } = triggerMetadata;
  const isCustomEventResourceType = resourceType === ResourceType.CustomEvent;

  const value = startUtils.getResourceValue({ ...nodeData, nodeClass: trigger });

  let placeholderValue = selectPlaceholder;
  if (displayValue) {
    placeholderValue = isCustomEventResourceType && value ? selectPlaceholder : displayValue;
  }

  const showLink = resourceHasLink && value && startUtils.isResourceValueValid(value);

  const handleChange = ({ value: updatedValue, label }: InternalOnChangeProp) => {
    let correctedValue = updatedValue;

    if (resourceValueType === 'number' && !Number.isFinite(updatedValue)) {
      correctedValue = Number(updatedValue);
    } else if (resourceValueType === 'string' && typeof updatedValue !== 'string') {
      correctedValue = `${updatedValue}`;
    }

    onChange({
      value: correctedValue,
      resourceName: label,
      label,
      fieldName: startUtils.getResourceField({ nodeClass: trigger }),
    });
  };

  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>();

  const ResourceField = getFieldComponent(resourceType);

  return (
    <>
      <StyledLeftTooltip isEnabled={isReadOnly && !!tooltipContent} tooltipContent={tooltipContent}>
        <ResourceField
          data-test={TRIGGER_DATA_ID}
          id={RESOURCE_ID}
          placeholder={placeholderValue}
          isError={areValidationsShown && !startUtils.isResourceValueValid(value)}
          nodeData={nodeData ?? ({} as unknown as StartNodeData)}
          {...triggerMetadata}
          value={value}
          onChange={handleChange}
          style={{ wordBreak: 'break-all' }}
          displayValue={displayValue}
          isDisabled={isReadOnly}
          setTooltipContent={setTooltipContent}
        />
      </StyledLeftTooltip>
      {showLink && <ViewListLink id="view-list-link" value={value} />}
    </>
  );
};

export default ResourceForm;
